import { useEffect, useState } from 'react';
import {
  Trophy, Star, Flame, Crown, Shield, Target, Users, Heart,
  Truck, Sunrise, Zap, FileCheck, UserPlus, Route,
  type LucideIcon,
} from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { LeaderboardCard } from '@/components/ui/LeaderboardCard';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useGamification } from '@/hooks/useGamification';
import { useProfile } from '@/hooks/useProfile';
import confetti from 'canvas-confetti';

const ICON_MAP: Record<string, LucideIcon> = {
  UserPlus, Route, Shield, Users, FileCheck, Flame, Crown,
  Sunrise, Truck, Target, Zap, Heart, Trophy, Star,
};

function BadgeIcon({ iconName, size = 24, className = '' }: { iconName: string; size?: number; className?: string }) {
  const Icon: LucideIcon = ICON_MAP[iconName] || Trophy;
  return <Icon size={size} className={className} />;
}

export function GamificationPage() {
  const { totalPoints, history, badges, leaderboard, loading } = useGamification();
  const { profile } = useProfile();
  const [showConfetti, setShowConfetti] = useState(false);

  // Confetti on first visit with earned badges
  useEffect(() => {
    const earned = badges.filter(b => b.earned);
    if (earned.length > 0 && !showConfetti) {
      setShowConfetti(true);
      confetti({
        particleCount: 80,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#FDD238', '#000000', '#FFFFFF'],
      });
    }
  }, [badges, showConfetti]);

  if (loading) return <LoadingSpinner />;

  const earnedBadges = badges.filter(b => b.earned);
  const lockedBadges = badges.filter(b => !b.earned);

  return (
    <div className="space-y-6 animate-fade-in">
      <h1 className="font-heading text-3xl tracking-wider">LOGROS</h1>

      {/* Points header */}
      <Card className="bg-brand-black text-brand-white text-center py-6">
        <Star size={32} className="text-brand-yellow mx-auto mb-2" />
        <p className="font-heading text-5xl tracking-wider text-brand-yellow">{totalPoints.toLocaleString()}</p>
        <p className="text-sm text-gray-400 mt-1">Puntos totales</p>
        {profile && profile.current_streak_days > 0 && (
          <div className="flex items-center justify-center gap-1 mt-3">
            <Flame size={16} className="text-brand-yellow" />
            <span className="text-sm text-brand-yellow font-semibold">Racha de {profile.current_streak_days} días</span>
          </div>
        )}
      </Card>

      {/* Earned badges */}
      <div>
        <h2 className="font-heading text-xl tracking-wider mb-3">BADGES DESBLOQUEADOS ({earnedBadges.length})</h2>
        {earnedBadges.length === 0 ? (
          <Card className="text-center py-6">
            <Trophy size={24} className="text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">Completa acciones para desbloquear badges</p>
          </Card>
        ) : (
          <div className="grid grid-cols-3 gap-3">
            {earnedBadges.map(badge => (
              <Card key={badge.id} className="flex flex-col items-center py-4 animate-confetti">
                <div className="w-14 h-14 bg-brand-yellow rounded-2xl flex items-center justify-center mb-2">
                  <BadgeIcon iconName={badge.icon_name} size={28} className="text-brand-black" />
                </div>
                <p className="text-xs font-semibold text-center">{badge.name}</p>
                {badge.points_value > 0 && (
                  <span className="text-[10px] text-brand-yellow font-bold mt-1">+{badge.points_value} pts</span>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Locked badges */}
      {lockedBadges.length > 0 && (
        <div>
          <h2 className="font-heading text-xl tracking-wider mb-3">POR DESBLOQUEAR ({lockedBadges.length})</h2>
          <div className="grid grid-cols-3 gap-3">
            {lockedBadges.map(badge => (
              <Card key={badge.id} className="flex flex-col items-center py-4 opacity-50">
                <div className="w-14 h-14 bg-gray-200 rounded-2xl flex items-center justify-center mb-2">
                  <BadgeIcon iconName={badge.icon_name} size={28} className="text-gray-400" />
                </div>
                <p className="text-xs font-medium text-center text-gray-500">{badge.name}</p>
                <p className="text-[10px] text-gray-400 text-center mt-0.5">{badge.description}</p>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Leaderboard */}
      {leaderboard.length > 0 && (
        <div>
          <h2 className="font-heading text-xl tracking-wider mb-3">TABLA DE POSICIONES</h2>
          <div className="space-y-2">
            {leaderboard.slice(0, 10).map(entry => (
              <LeaderboardCard
                key={entry.driver_profile_id}
                rank={entry.rank}
                name={entry.display_name || 'Conductor'}
                photoUrl={entry.profile_photo_url}
                points={entry.gamification_points}
                subtitle={`${entry.total_routes_completed} rutas`}
                isCurrentUser={entry.driver_profile_id === profile?.id}
              />
            ))}
          </div>
        </div>
      )}

      {/* Recent activity */}
      {history.length > 0 && (
        <div>
          <h2 className="font-heading text-xl tracking-wider mb-3">HISTORIAL DE PUNTOS</h2>
          <div className="space-y-1">
            {history.slice(0, 15).map(entry => (
              <div key={entry.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                <div>
                  <p className="text-sm">{entry.action_code.replace(/_/g, ' ')}</p>
                  <p className="text-xs text-gray-400">{new Date(entry.created_at).toLocaleDateString('es-MX')}</p>
                </div>
                <span className="text-sm font-bold text-brand-yellow">+{entry.points}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
